export interface HttpRequestOptions extends RequestInit {
  timeoutMs?: number
  retries?: number
}

export class HttpError extends Error {
  status?: number
  payload?: unknown

  constructor(message: string, status?: number, payload?: unknown) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.payload = payload
  }
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    return response.json()
  }
  const text = await response.text()
  return text || null
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function shouldRetry(error: unknown): boolean {
  if (!(error instanceof HttpError)) {
    return true
  }
  if (!error.status) {
    return true
  }
  return error.status >= 500
}

export async function requestJson<T>(url: string, options: HttpRequestOptions = {}): Promise<T> {
  const { timeoutMs = 10000, retries = 0, ...init } = options

  let attempt = 0
  while (attempt <= retries) {
    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => {
      controller.abort()
    }, timeoutMs)

    try {
      const response = await fetch(url, {
        ...init,
        signal: controller.signal,
      })
      const payload = await parseResponseBody(response)

      if (!response.ok) {
        const message =
          (payload && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string'
            ? payload.error
            : `请求失败: ${response.status}`)
        throw new HttpError(message, response.status, payload)
      }

      return payload as T
    } catch (error) {
      if (attempt >= retries || !shouldRetry(error)) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          throw new HttpError('请求超时，请稍后重试')
        }
        throw error
      }
      await sleep(300 * (attempt + 1))
    } finally {
      window.clearTimeout(timeoutId)
    }

    attempt += 1
  }

  throw new HttpError('请求失败，请稍后重试')
}
